// Type-only: the card module pulls in icons, which these pure helpers' tests have no reason to load.
import type { TransferSpeed } from "@/utils/cctp/v2/fees";
import type { IrisDelayReason } from "@/utils/cctp/v2/iris";
import { parseCCTPMessageV2 } from "@/utils/cctp/v2/message";
import { v2DepositEtaSeconds } from "@/utils/cctp/v2/speed-quote";

import type { TransferProgressData } from "./transfer-progress-card";

const BLOCK_RE = /^\d+$/;

interface RenewalFields {
  sourceChain?: string;
  speed?: TransferSpeed;
  estimatedBridgeTimeSeconds?: number;
  renewedFromExpirationBlock?: string;
  delayReason?: IrisDelayReason;
}

/** The fields of a deposit card these helpers change. */
type DepositCard = Extract<TransferProgressData, { type: "cctp-deposit" }>;
export type RenewalUpdate = Partial<
  Pick<
    DepositCard,
    | "currentStep"
    | "attestation"
    | "messageBytes"
    | "renewedFromExpirationBlock"
    | "delayReason"
    | "estimatedBridgeTimeSeconds"
  >
>;

type AttestationResult =
  | { ready: true; attestation: string; messageBytes: string | null }
  | { ready: false; delayReason?: IrisDelayReason };

/**
 * The card update once Circle starts renewing an expired attestation: back to bridging, with the
 * expired copy dropped. The expiry it renews is kept so polling can tell the renewed copy from
 * the stale one Circle serves until the renewal lands.
 */
export function renewedCardUpdate(messageBytes: string): RenewalUpdate {
  const parsed = parseCCTPMessageV2(messageBytes);
  if (!parsed) throw new Error("Can't renew a message that isn't CCTP v2");
  return {
    currentStep: "bridging",
    attestation: undefined,
    messageBytes: undefined,
    // A string: the card is persisted as JSON, which has no bigint.
    renewedFromExpirationBlock: parsed.expirationBlock.toString(),
  };
}

/** What a renewed card's bridging poll needs to ignore the stale attestation. */
export function renewalPollOptions({ renewedFromExpirationBlock }: RenewalFields): {
  renewedFrom?: bigint;
} {
  if (!renewedFromExpirationBlock || !BLOCK_RE.test(renewedFromExpirationBlock)) return {};
  return { renewedFrom: BigInt(renewedFromExpirationBlock) };
}

/** The card update from one bridging poll, or `null` when nothing changed. */
export function bridgingUpdate(
  card: RenewalFields,
  result: AttestationResult,
): RenewalUpdate | null {
  if (result.ready) {
    return {
      currentStep: "claimable",
      attestation: result.attestation,
      ...(result.messageBytes && { messageBytes: result.messageBytes }),
      renewedFromExpirationBlock: undefined,
      delayReason: undefined,
    };
  }
  // Only a Fast transfer has a faster estimate for Circle's delay to invalidate.
  if (!result.delayReason || card.speed !== "fast" || card.delayReason === result.delayReason) {
    return null;
  }
  return {
    delayReason: result.delayReason,
    estimatedBridgeTimeSeconds: v2DepositEtaSeconds(card.sourceChain ?? "", "standard"),
  };
}
